import { INVALID_MOVE } from 'boardgame.io/core';
import { debug } from '../../utils/debug';
import type { Ctx } from 'boardgame.io';
import type { SeattleGameState, SelectActionSeattleArgs } from '../../types';
import { validateSelectActionSeattle } from '../validators/selectActionsSeattleValidator';

/**
 * アクション選択Move（Seattle固有）
 *
 * @param G - Seattle ゲーム状態
 * @param ctx - Boardgame.io コンテキスト
 * @param args - アクション選択引数
 * @returns void or INVALID_MOVE
 *
 * 処理内容:
 * 1. バリデーション
 * 2. アクション選択を記録
 * 3. 使用回数を+1
 * 4. LOCOMOTIVEの場合は効果を即座に適用
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_03_select_actions_seattle.md
 */
export function selectActionSeattle(
  { G, ctx }: { G: SeattleGameState; ctx: Ctx },
  args: SelectActionSeattleArgs
): void | typeof INVALID_MOVE {
  // バリデーション
  const validation = validateSelectActionSeattle(G, ctx, args);
  if (!validation.success) {
    console.error(`[selectActionSeattle] Validation failed: ${validation.error} - ${validation.message}`);
    return INVALID_MOVE;
  }

  const player = G.players['0'];
  const action = args.action;

  // アクション選択を記録
  player.actionsSelectedThisTurn.push(action);

  // selectedAction は ENGINEER を優先（#11: Engineer→Locomotive 順で ENGINEER が上書きされるバグ修正）
  player.selectedAction = player.actionsSelectedThisTurn.includes('ENGINEER')
    ? 'ENGINEER' : action;

  // 使用回数を+1
  if (action === 'ENGINEER') {
    player.engineerUsageCount += 1;
    debug.log(`[selectActionSeattle] Player selected ENGINEER, usage count: ${player.engineerUsageCount}`);
  } else if (action === 'LOCOMOTIVE') {
    player.locomotiveUsageCount += 1;

    // Locomotive 効果を記録（Cancel時の巻き戻し用）
    player.locomotiveEffectThisTurn = args.locomotiveEffect;

    // Locomotive 効果を適用
    if (args.locomotiveEffect === 'LOCO_UP') {
      player.locomotiveLevel += 1;
      debug.log(`[selectActionSeattle] Player selected LOCOMOTIVE (LOCO_UP), level: ${player.locomotiveLevel}`);
    } else if (args.locomotiveEffect === 'CARGO_UP') {
      G.cargoTransportLevel += 1;
      debug.log(`[selectActionSeattle] Player selected LOCOMOTIVE (CARGO_UP), cargo level: ${G.cargoTransportLevel}`);
    }
  }

  debug.log(`[selectActionSeattle] Actions selected this turn: ${player.actionsSelectedThisTurn.join(', ')}`);
}

/**
 * アクション選択確定Move（Seattle固有）
 *
 * 選択を確定してPhaseを終了する。
 * アクション選択なしでも確定可能（0個選択で次のPhaseへ）。
 *
 * @param G - Seattle ゲーム状態
 * @param _ctx - Boardgame.io コンテキスト
 * @returns void or INVALID_MOVE
 */
export function confirmSelectionSeattle(
  { G, ctx: _ctx }: { G: SeattleGameState; ctx: Ctx }
): void | typeof INVALID_MOVE {
  const player = G.players['0'];

  // パスフラグを立ててPhase終了を促す
  player.hasPassed = true;

  debug.log(`[confirmSelectionSeattle] Selection confirmed, actions: ${player.actionsSelectedThisTurn.join(', ') || 'none'}`);
}

/**
 * アクション選択キャンセルMove（Seattle固有）
 *
 * 選択済みアクションの効果を元に戻し、選択状態をリセットする。
 *
 * @param G - Seattle ゲーム状態
 * @param _ctx - Boardgame.io コンテキスト
 * @returns void or INVALID_MOVE
 */
export function cancelSelectionSeattle(
  { G, ctx: _ctx }: { G: SeattleGameState; ctx: Ctx }
): void | typeof INVALID_MOVE {
  const player = G.players['0'];

  // 選択が0個の場合はキャンセルする意味がない
  if (player.actionsSelectedThisTurn.length === 0) {
    return INVALID_MOVE;
  }

  // 各アクションの効果を元に戻す
  for (const action of player.actionsSelectedThisTurn) {
    if (action === 'ENGINEER') {
      player.engineerUsageCount -= 1;
    } else if (action === 'LOCOMOTIVE') {
      player.locomotiveUsageCount -= 1;
      // 適用済みの効果を元に戻す
      if (player.locomotiveEffectThisTurn === 'LOCO_UP') {
        player.locomotiveLevel -= 1;
      } else if (player.locomotiveEffectThisTurn === 'CARGO_UP') {
        G.cargoTransportLevel -= 1;
      }
    }
  }

  // 選択状態をリセット
  player.actionsSelectedThisTurn = [];
  player.selectedAction = null;
  player.locomotiveEffectThisTurn = undefined;

  debug.log(`[cancelSelectionSeattle] Selection cancelled, all effects reverted`);
}
